import { UserType } from '../../entities/tenant/user.type';
import { AIAgentSecurityModule } from '../modules/ai.agent.security.module';
import { AppSecurityModule } from '../modules/app.security';
import { AssetSecurityModule } from '../modules/asset.security.module';
import { ClaimSecurityModule } from '../modules/claim.security.module';
import { CommandCenterSecurityModule } from '../modules/command.center.security.module';
import { ConfigurationSecurityModule, ConfigurationSecurityRight } from '../modules/configuration.security.module';
import { withDerivedCrmRecordRights } from '../modules/crm.record.rights';
import { CrmSecurityModule } from '../modules/crm.security.module';
import { DefectSecurityModule } from '../modules/defect.security.module';
import { withFieldAppReleaseRights } from '../modules/field.app.section.rights';
import { FinanceSecurityModule } from '../modules/finance.security.module';
import { InboundEmailSecurityModule } from '../modules/inbound.email.security.module';
import { IndividualComplianceSecurityModule } from '../modules/individual.compliance.security.module';
import { IntegrationSecurityModule } from '../modules/integration.security.module';
import { JobSecurityModule } from '../modules/job.security.module';
import { withDerivedJobSectionRights } from '../modules/job.section.rights';
import { JobTemplatesSecurityModule } from '../modules/job.template.security.module';
import { MobileTechniciansSecurityModule } from '../modules/mobile.technicians.security.module';
import { SalesSecurityModule } from '../modules/sales.security.module';
import { QuoteSecurityModule } from '../modules/quote.security.module';
import { RecurringJobSecurityModule } from '../modules/recurring.job.security.module';
import { ReportSecurityModule } from '../modules/report.security.module';
import { SchedulerSecurityModule } from '../modules/scheduler.security.module';
import { TaskSecurityModule } from '../modules/task.security.module';
import { TimesheetSecurityModule } from '../modules/timesheet.security.module';
import { VariationSecurityModule } from '../modules/variation.security.module';
import { WorkComplianceSecurityModule } from '../modules/work.compliance.security.module';
import { DashboardSecurityModule } from '../modules/dashboard.security.module';
import { QuoteTemplatesSecurityModule } from '../modules/quote.template.security.module';
import { FormSecurityModule } from '../modules/form.security.module';
import { EquipmentSecurityModule } from '../modules/equipment.security.module';
import { SecurityRole, SecurityRoleType } from '../security.role';

export class EmployeeAdministrator implements SecurityRole {

    _id = '886da031-c239-4cfe-9b69-4067355a43b5';
    system = true;
    hidden = false;
    type = UserType.Employee;
    name = SecurityRoleType.EmployeeAdministrator;
    description = 'Full access to everything in the system. Can edit and delete records. Can view all logs.';
    rights = withDerivedCrmRecordRights(withFieldAppReleaseRights(withDerivedJobSectionRights([
        ...new AppSecurityModule().allRights,
        // Includes AllowOutOfZoneSelection — the right is allow-style, so admins need it granted to stay unrestricted.
        // Includes MarkFinanceLock, matching how ModifyLockedItems is granted here: every other seeded
        // role excludes it, so a consultant still opts each non-admin role in.
        // Includes ApprovePendingItems: pending lines hold a stage's claim, so the administrator must always be able to decide them (WBS-12230).
        // Includes ApproveClaimsWithUnresolvedTasks: the administrator is its default holder (WBS-12226).
        // Includes OverrideCompletionGates: the administrator is its default holder (WBS-12200).
        // Includes CompleteStagesWithUnresolvedTasks: the administrator is its default holder (WBS-12220).
        // Includes ChangeJobStructure: out of the box only an administrator overrides a new job's
        // suggested structure or converts an existing one; a consultant opts other roles in.
        ...new JobSecurityModule().allRights,
        ...new ConfigurationSecurityModule().allExceptRights([
            ConfigurationSecurityRight.ManageBilling,
            ConfigurationSecurityRight.AccessBilling,
        ]),
        ...new FinanceSecurityModule().allRights,
        ...new InboundEmailSecurityModule().allRights,
        ...new IndividualComplianceSecurityModule().allRights,
        ...new QuoteSecurityModule().allRights,
        ...new TimesheetSecurityModule().allRights,
        ...new TaskSecurityModule().allRights,
        ...new CrmSecurityModule().allRights,
        ...new SchedulerSecurityModule().allRights,
        // ...new PurchaseOrderSecurityModule().allRights,
        ...new WorkComplianceSecurityModule().allRights,
        ...new ClaimSecurityModule().allRights,
        ...new CommandCenterSecurityModule().allRights,
        ...new ReportSecurityModule().allRights,
        ...new VariationSecurityModule().allRights,
        ...new DefectSecurityModule().allRights,
        ...new AssetSecurityModule().allRights,
        ...new EquipmentSecurityModule().allRights,
        ...new RecurringJobSecurityModule().allRights,
        ...new JobTemplatesSecurityModule().allRights,
        ...new QuoteTemplatesSecurityModule().allRights,
        ...new SalesSecurityModule().allRights,
        ...new DashboardSecurityModule().allRights,

        // Not sure if they should access this
        ...new MobileTechniciansSecurityModule().allRights,

        ...new FormSecurityModule().allRights,
        ...new AIAgentSecurityModule().allRights,
        ...new IntegrationSecurityModule().allRights,
    ])));

}

export const EmployeeOwnerRoleId = '10000000-0000-0000-0000-000000000001'
export class EmployeeOwner extends EmployeeAdministrator {
    constructor() {
        super();
        this._id = EmployeeOwnerRoleId;
        this.system = true;
        this.hidden = true;
        this.type = UserType.Employee;
        this.name = SecurityRoleType.EmployeeOwner;
        this.description = 'Owner has full access to everything in the system. Can edit and delete records. Can view all logs. Can manage billing and payments.';
        this.rights = [
            ...this.rights,
            ...new ConfigurationSecurityModule().allRights,
        ];
    }
}
